import { create } from 'zustand';

/**
 * Dashboard Scope Store — Phase C C6 (D7).
 *
 * Tracks the scope dropdown the Topbar exposes for the dashboard:
 *   - `scope: 'federal'` (no state filter — national rollups)
 *   - `scope: 'state'` (a specific 2-letter USPS code)
 *
 * Default: derived from the authenticated user's `stateTerritory` field
 * on first mount via `deriveDefaultScope`. Falls back to 'federal' for
 * users without a state (federal personas, anon flows).
 *
 * The DashboardPanel + DashboardStateMap subscribe to this store and
 * re-fetch tile data with `?state=XX` when scope changes. The store is
 * intentionally separate from the legacy `topbarOrg` (Federal / State /
 * Local / Private) selector in `uiStore`, which is an *organization*
 * dimension. Scope is per-view and ephemeral.
 */
export type DashboardScopeKind = 'federal' | 'state';

export interface DashboardScope {
  scope: DashboardScopeKind;
  /** USPS 2-letter code when `scope === 'state'`. */
  state?: string;
}

interface DashboardScopeState extends DashboardScope {
  /** True once `setFromUser` has been called for the current session. */
  _initialised: boolean;
  setScope: (scope: DashboardScope) => void;
  /** Initialise from auth user. No-op if already initialised. */
  setFromUser: (input: {
    stateTerritory?: string | null;
    persona?: string | null;
  }) => void;
  reset: () => void;
}

const STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC',
  florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID', illinois: 'IL',
  indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY', louisiana: 'LA',
  maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
  mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI',
  wyoming: 'WY',
};

/** Normalize a state-like string (USPS / full name, case-insensitive). */
export function normalizeStatePostal(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const v = raw.trim();
  if (v.length === 2 && /^[A-Za-z]{2}$/.test(v)) return v.toUpperCase();
  const code = STATE_NAME_TO_CODE[v.toLowerCase()];
  return code ?? null;
}

/**
 * Derivation logic exported for unit tests. Given a user's stateTerritory
 * + persona, returns the default DashboardScope.
 */
export function deriveDefaultScope(input: {
  stateTerritory?: string | null;
  persona?: string | null;
}): DashboardScope {
  // Federal personas always default to federal scope.
  const persona = (input.persona ?? '').toLowerCase();
  if (persona === 'federal' || persona.startsWith('federal-')) {
    return { scope: 'federal' };
  }
  const code = normalizeStatePostal(input.stateTerritory);
  if (code) return { scope: 'state', state: code };
  return { scope: 'federal' };
}

const INITIAL: DashboardScope = { scope: 'federal', state: undefined };

export const useDashboardScopeStore = create<DashboardScopeState>((set, get) => ({
  ...INITIAL,
  _initialised: false,

  setScope: (scope) =>
    set({
      scope: scope.scope,
      state: scope.scope === 'state' ? scope.state : undefined,
      _initialised: true,
    }),

  setFromUser: (input) => {
    if (get()._initialised) return;
    const derived = deriveDefaultScope(input);
    set({ ...derived, _initialised: true });
  },

  reset: () => set({ ...INITIAL, _initialised: false }),
}));

/**
 * Convenience hook returning the scope plus a derived "stateForApi" token
 * suitable for `?state=` query params. Returns `undefined` for federal.
 */
export function useDashboardScope(): {
  scope: DashboardScopeKind;
  state?: string;
  stateForApi?: string;
} {
  const scope = useDashboardScopeStore((s) => s.scope);
  const state = useDashboardScopeStore((s) => s.state);
  return {
    scope,
    state,
    stateForApi: scope === 'state' ? state : undefined,
  };
}
